import { useNavigate } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useAiDataPolicyRole } from '@shared/ai-data-policy/access';
import { formatPolicyDate, policyValueKey } from '@shared/ai-data-policy/labels';
import { bidiIsolate } from '@shared/ai-data-policy/names';
import { useAiDataPolicyStore } from '@shared/ai-data-policy/store';
import { Button } from '@shared/components/ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@shared/components/ui/Popover';

import { goToDataPolicySettings } from '../data-policy/navigation';
import { usePolicyPromptStore } from '../data-policy/prompt.store';
import type { DataCategory, MessageRoute, SharedCategory } from '../types';

import { ProviderLogo } from './ModelMark';

function lowerDataLabel(
  t: (key: string, fallback: string) => string,
  categories: DataCategory[],
): string {
  const investor = categories.includes('investor');
  const crm = categories.includes('crm');
  if (investor && crm) return t('answerDetails.data.both', 'investor and CRM data');
  if (crm) return t('answerDetails.data.crm', 'CRM data');
  return t('answerDetails.data.investor', 'investor data');
}

function joinShared(
  t: (key: string, fallback: string) => string,
  categories: SharedCategory[],
  and: string,
): string {
  const items = categories.map((c) => t(`answerDetails.shared.${c}`, c));
  if (items.length === 0) return '';
  const head = items.slice(0, -1).join(', ');
  const last = items[items.length - 1] ?? '';
  const sentence = head ? `${head} ${and} ${last}` : last;
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{children}</dd>
    </div>
  );
}

export interface AnswerDetailsPopoverProps {
  route: MessageRoute;
  onAskInNewChat?: () => void;
  children: ReactNode;
}

export function AnswerDetailsPopover({
  route,
  onAskInNewChat,
  children,
}: AnswerDetailsPopoverProps) {
  const { t, i18n } = useTranslation('ai-cio');
  const { t: tp } = useTranslation('ai-data-policy');
  const navigate = useNavigate();
  const isAdmin = useAiDataPolicyRole() === 'admin';
  const undecided = useAiDataPolicyStore((s) => (s.policy?.value ?? 'not_chosen') === 'not_chosen');
  const [open, setOpen] = useState(false);

  const internal = route.modelUsed.kind === 'internal';
  const data = lowerDataLabel(t, route.categories);
  const why = t(`answerDetails.reasons.${route.reasonCode}`, {
    model: route.selectedModel.label,
    data,
    provider: route.selectedModel.providerLabel,
  });
  const sentValue = internal
    ? t('answerDetails.nothing', 'Nothing')
    : `${joinShared(t, route.sharedCategories, t('answerDetails.and', 'and'))}${
        route.categories.length === 0
          ? ` ${t('answerDetails.noInvestorData', 'No investor or CRM data.')}`
          : ''
      }`;
  const policyValue = tp(policyValueKey(route.policy.value));
  const policyDate = formatPolicyDate(route.policy.setAt);
  const policyLine =
    route.policy.value === 'not_chosen'
      ? t('answerDetails.policyNotChosen', 'Not chosen yet · Investbanq model only by default')
      : route.policy.setByName
        ? t('answerDetails.policySetBy', '{{value}} · set by {{name}} on {{date}}', {
            value: policyValue,
            name: bidiIsolate(route.policy.setByName, i18n.dir() === 'rtl'),
            date: policyDate,
          })
        : policyDate
          ? t('answerDetails.policySetOn', '{{value}} · set on {{date}}', {
              value: policyValue,
              date: policyDate,
            })
          : policyValue;
  const canChoose = undecided && isAdmin;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={t('meta.answeredBy', 'Answered by {{model}}. Show answer details', {
            model: route.modelUsed.label,
          })}
          title={t('meta.tooltip', 'Answer details')}
          className="ms-1 flex h-6 max-w-[180px] items-center gap-1 rounded-md px-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        aria-label={t('answerDetails.title', 'About this answer')}
        data-ai-cio-surface
        onEscapeKeyDown={(e) => {
          e.stopPropagation();
        }}
        className="w-[320px] rounded-lg p-4"
      >
        <p className="text-sm font-medium text-foreground">
          {t('answerDetails.title', 'About this answer')}
        </p>
        <dl className="mt-3 flex flex-col gap-3">
          <Row label={t('answerDetails.answeredBy', 'Answered by')}>
            <span className="flex items-center gap-1.5">
              <span className="flex shrink-0 text-foreground">
                <ProviderLogo group={route.modelUsed.group} />
              </span>
              {route.modelUsed.label}
            </span>
            <span className="block text-xs text-muted-foreground">
              {internal
                ? t('answerDetails.runsInside', 'Runs inside Investbanq')
                : t('answerDetails.runsOutside', '{{provider}} · runs outside Investbanq', {
                    provider: route.modelUsed.providerLabel,
                  })}
            </span>
          </Row>
          <Row label={t('answerDetails.why', 'Why this model')}>{why}</Row>
          {route.dataUsed && (
            <Row
              label={
                route.dataUsedKind === 'crm'
                  ? t('answerDetails.dataUsedCrm', 'CRM data used')
                  : t('answerDetails.dataUsedInvestor', 'Investor data used')
              }
            >
              {route.dataUsed}
            </Row>
          )}
          <Row
            label={
              internal
                ? t('answerDetails.sentOutside', 'Sent outside Investbanq')
                : t('answerDetails.sentTo', 'Sent to {{provider}}', {
                    provider: route.modelUsed.providerLabel,
                  })
            }
          >
            {sentValue}
          </Row>
          <Row label={t('answerDetails.policy', 'Data policy')}>{policyLine}</Row>
        </dl>
        <div className="my-3 h-px bg-border" aria-hidden />
        <div className="flex flex-wrap gap-2">
          {(route.reasonCode === 'R1b' || route.reasonCode === 'R1c') && onAskInNewChat && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setOpen(false);
                onAskInNewChat();
              }}
            >
              {t('answerDetails.askInNewChat', 'Ask in a new chat')}
            </Button>
          )}
          {canChoose ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setOpen(false);
                void usePolicyPromptStore.getState().open({
                  variant: 'manual',
                  investorName: null,
                  categories: [],
                  selectedModel: route.selectedModel,
                });
              }}
            >
              {t('answerDetails.choosePolicy', 'Choose data policy…')}
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setOpen(false);
                goToDataPolicySettings(navigate);
              }}
            >
              {t('answerDetails.viewPolicy', 'View data policy')}
              <ArrowRight className="rtl:-scale-x-100" aria-hidden />
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
